// v16.34 — Shared BackButton component cho public/athlete pages.
//
// Behavior:
//   - Nếu window.history.length > 1 (có trang trước trong session) → navigate(-1).
//   - Nếu không có history (user vào từ link ngoài / bookmark / mở tab mới) →
//     fallback về `/`.
//
// Style: absolute top-left với arrow icon + nhãn ngắn. Phù hợp overlay lên
// bất kỳ page nào (dark hoặc light bg). Prop `variant`:
//   - "floating" (default): absolute position với backdrop-blur, dùng khi
//     page có gradient/hero background.
//   - "inline": relative position, dùng khi muốn embed vào header có sẵn.
//   - "solid": nền đen mờ chắc chắn nhìn thấy, dùng khi bg là ảnh/video.
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";

export default function BackButton({
  label = "Quay lại",
  fallback = "/",
  variant = "floating",
  className = "",
  testId = "back-button",
}) {
  const navigate = useNavigate();

  const handleClick = useCallback(() => {
    // history.length >= 2 means there's at least one previous entry in this
    // tab (initial "about:blank" + current page = 2 for direct navigation
    // in some browsers, so we check > 1 conservatively).
    if (typeof window !== "undefined" && window.history.length > 1) {
      // Attempt navigate(-1). Nếu previous entry cũng thuộc app thì OK;
      // nếu previous là external (google.com…), navigate(-1) sẽ leave app —
      // trong WebView native thì user muốn quay về link cũ là hợp lý.
      navigate(-1);
    } else {
      navigate(fallback, { replace: true });
    }
  }, [navigate, fallback]);

  const baseCls =
    "inline-flex items-center gap-2 rounded-full font-bold text-[13px] " +
    "transition active:scale-[0.96] focus:outline-none focus-visible:ring-2 " +
    "focus-visible:ring-orange-400/60";

  // iOS/Android safe-area: dùng --pl-safe-top var injected bởi native
  // WebView; fallback env(safe-area-inset-top) cho browser hiện đại;
  // cuối cùng 1rem cho web thường.
  const floatingStyle =
    variant === "inline"
      ? undefined
      : {
          top: "max(0.75rem, calc(var(--pl-safe-top, env(safe-area-inset-top, 0px)) + 0.5rem))",
          left: "max(0.75rem, calc(var(--pl-safe-left, env(safe-area-inset-left, 0px)) + 0.5rem))",
        };

  let styleCls;
  if (variant === "inline") {
    styleCls =
      "relative px-3 py-1.5 bg-white/8 hover:bg-white/14 text-white/85 " +
      "border border-white/10";
  } else if (variant === "solid") {
    styleCls =
      "fixed z-50 px-3.5 py-2 bg-black/70 hover:bg-black/85 " +
      "text-white shadow-lg";
  } else {
    // floating default
    styleCls =
      "fixed z-50 px-3.5 py-2 bg-white/10 hover:bg-white/18 " +
      "text-white backdrop-blur-md border border-white/12 shadow-lg";
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`${baseCls} ${styleCls} ${className}`}
      style={floatingStyle}
      data-testid={testId}
      aria-label={label}
    >
      <ArrowLeft size={14} weight="bold" />
      {label ? <span>{label}</span> : null}
    </button>
  );
}
